import React, { useState } from 'react';
import emailjs from 'emailjs-com';
import { FiMail, FiPhone, FiMapPin, FiLinkedin } from 'react-icons/fi';
import SuccessPopup from './SuccessPopup';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID_YOU = process.env.REACT_APP_EMAILJS_TEMPLATE_YOU;
const TEMPLATE_ID_USER = process.env.REACT_APP_EMAILJS_TEMPLATE_USER;
const USER_ID = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const Contact = ({ hideTitle = false }) => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('SENDING');

    // Send both emails in parallel
    const mainEmail = emailjs.send(SERVICE_ID, TEMPLATE_ID_YOU, form, USER_ID);
    const autoReply = emailjs.send(SERVICE_ID, TEMPLATE_ID_USER, form, USER_ID);

    Promise.all([mainEmail, autoReply])
      .then(() => {
        setStatus('SUCCESS');
        setForm({ name: '', email: '', subject: '', message: '' });
        setShowSuccessPopup(true);
      })
      .catch((err) => {
        console.error('EmailJS error:', err);
        // If main email succeeded but auto-reply failed, still show success
        mainEmail.then(() => {
          setStatus('SUCCESS');
          setForm({ name: '', email: '', subject: '', message: '' });
          setShowSuccessPopup(true);
        }).catch(() => {
          setStatus('ERROR');
        });
      });
  };

  return (
    <section id="contact" className="section-padding bg-white dark:bg-dark-100 transition-colors duration-300">
      <div className="container-custom">
        {!hideTitle && (
        <>
        <h2 className="text-4xl font-bold text-secondary-800 dark:text-white mb-4 text-center">
          Contact <span className="gradient-text">Me</span>
        </h2>
        <p className="text-lg text-secondary-600 dark:text-gray-100 max-w-2xl mx-auto mb-12 text-center">
          Feel free to reach out to me for any questions or opportunities.
        </p>
        </>
        )}

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-secondary-800 dark:text-white mb-6">
              Get in Touch
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-lg bg-secondary-100 dark:bg-dark-300 hover:bg-secondary-200 dark:hover:bg-dark-400 transition-colors duration-200">
                <FiMail className="w-8 h-8 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-secondary-800 dark:text-white">Email</div>
                  <a href="mailto:ashar.sqa@gmail.com" className="text-secondary-600 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200">
                    ashar.sqa@gmail.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 rounded-lg bg-secondary-100 dark:bg-dark-300 hover:bg-secondary-200 dark:hover:bg-dark-400 transition-colors duration-200">
                <FiPhone className="w-8 h-8 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-secondary-800 dark:text-white">Phone</div>
                  <a href="tel:+923017022784" className="text-secondary-600 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200">
                    (+92) 03017022784
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 rounded-lg bg-secondary-100 dark:bg-dark-300 hover:bg-secondary-200 dark:hover:bg-dark-400 transition-colors duration-200">
                <FiMapPin className="w-8 h-8 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-secondary-800 dark:text-white">Location</div>
                  <div className="text-secondary-600 dark:text-gray-100">Lahore, Pakistan</div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 rounded-lg bg-secondary-100 dark:bg-dark-300 hover:bg-secondary-200 dark:hover:bg-dark-400 transition-colors duration-200">
                <FiLinkedin className="w-8 h-8 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-secondary-800 dark:text-white">LinkedIn</div>
                  <a 
                    href="https://www.linkedin.com/in/ashar-tariq/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-secondary-600 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  >
                    linkedin.com/in/ashar-tariq/
                  </a>
                </div>
              </div>
            </div>

            {/* Additional info */}
            <div className="mt-8 p-6 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-dark-200 dark:to-dark-300 rounded-xl">
              <h4 className="text-lg font-semibold text-secondary-800 dark:text-white mb-3">
                Why Work With Me?
              </h4>
              <ul className="space-y-2 text-secondary-600 dark:text-gray-100">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  Quick response time (within 24 hours)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  Proven track record in QA automation
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  Experience across multiple domains
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  Strong communication skills
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white dark:bg-dark-200 rounded-2xl p-8 shadow-lg border border-secondary-200 dark:border-dark-300">
            <h3 className="text-2xl font-bold text-secondary-800 dark:text-white mb-6">
              Send a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block mb-2 text-secondary-700 dark:text-white font-medium">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-secondary-100 dark:bg-dark-300 text-secondary-800 dark:text-white placeholder-secondary-400 dark:placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                    placeholder="Your name"
                    value={form.name}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block mb-2 text-secondary-700 dark:text-white font-medium">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-secondary-100 dark:bg-dark-300 text-secondary-800 dark:text-white placeholder-secondary-400 dark:placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                    placeholder="your.email@example.com"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="subject" className="block mb-2 text-secondary-700 dark:text-white font-medium">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-secondary-100 dark:bg-dark-300 text-secondary-800 dark:text-white placeholder-secondary-400 dark:placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                  placeholder="What's this about?"
                  value={form.subject}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block mb-2 text-secondary-700 dark:text-white font-medium">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg bg-secondary-100 dark:bg-dark-300 text-secondary-800 dark:text-white placeholder-secondary-400 dark:placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 resize-none"
                  placeholder="Tell me about your project or question..."
                  value={form.message}
                  onChange={handleChange}
                />
              </div>
              
              <button
                type="submit"
                disabled={status === 'SENDING'}
                className={`w-full py-3 rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-all duration-200 shadow-lg touch-target ${status === 'SENDING' ? 'bg-primary-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700 transform hover:-translate-y-1 hover:shadow-xl'}`}
              >
                <FiMail className="w-5 h-5" />
                {status === 'SENDING' ? 'Sending...' : 'Send Message'}
              </button>
              
              {status === 'ERROR' && (
                <div className="text-red-600 dark:text-red-400 mt-2 text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  Something went wrong. Please try again later.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
      
      {/* Success Popup */}
      <SuccessPopup
        isVisible={showSuccessPopup}
        message="Your message has been sent successfully! I'll get back to you soon."
        onClose={() => setShowSuccessPopup(false)}
        autoCloseDelay={4000}
      />
    </section>
  );
};

export default Contact;
